import { FormEvent, useState, useEffect, ChangeEvent } from 'react';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';
import { AddPaymentType, defaultAddPaymentType } from '../types/paymentValidations';
import { Button, Input } from '../../../shared';
import { addPaymentMethod } from '../../../store/paymentMethods/paymentMethodsSlice';
import { createPaymentMethod, getFortisClientToken } from '../../../api/paymentMethods';
import {
  ButtonsWrapper,
  CancelButton,
  Title,
  Wrapper,
  SpinnerOverlay,
  Spinner,
  ExpirationDateWrapper,
  MonthSelect,
  YearSelect
} from './CreatePaymentMethod.styled';
import { ErrorText, FullRow, HalfRow } from '../Forms.styled';
import { Wrapper as InputWrapper, Label, ErrorText as InputErrorText } from '../../../shared/Input/Input.styled';
import type { state as StateType } from '../../../types/store/state';
import { handleToastAsyncAction } from '../../../utils/handleToastAsyncAction';
import { setDefaultMethod } from '../../../api/paymentMethods';

import * as Sentry from '@sentry/react';

interface Props {
  setShowAddPayment: (value: boolean) => void;
}

export const CreatePaymentMethod = (props: Props) => {
  const { t } = useTranslation();
  const { primaryColor } = useSelector((state: StateType) => state.ui.pallette);
  const { paymentProcessor } = useSelector((state: StateType) => state.branchStore);
  const dispatch = useDispatch();

  const [errors, setErrors] = useState<AddPaymentType>(defaultAddPaymentType);
  const [cardNumber, setCardNumber] = useState<string>('');
  const [nameOnCard, setNameOnCard] = useState<string>('');
  const [cvc, setCvc] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [expMonth, setExpMonth] = useState<string>('');
  const [expYear, setExpYear] = useState<string>('');

  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  const months = Array.from({ length: 13 - currentMonth }, (_, i) => {
    const month = currentMonth + i;
    return {
      value: month < 10 ? `0${month}` : `${month}`,
      label: month < 10 ? `0${month}` : `${month}`,
    };
  });

  const years = Array.from({ length: 20 }, (_, i) => {
    const year = currentYear + i;
    return {
      value: `${year}`,
      label: `${year}`,
    };
  });

  const getValidYears = (selectedMonth: string) => {
    if (!selectedMonth) return years;
    
    const monthNum = parseInt(selectedMonth);
    const currentMonthNum = currentMonth;
    const currentYearNum = currentYear;

    if (monthNum < currentMonthNum) {
      return Array.from({ length: 20 }, (_, i) => {
        const year = currentYearNum + i + 1;
        return {
          value: `${year}`,
          label: `${year}`,
        };
      });
    }

    return years;
  };

  const validYears = getValidYears(expMonth);

  const getValidMonths = (selectedYear: string) => {
    if (!selectedYear) return months;
    
    const yearNum = parseInt(selectedYear);
    const currentYearNum = currentYear;

    if (yearNum > currentYearNum) {
      return Array.from({ length: 12 }, (_, i) => {
        const month = i + 1;
        return {
          value: month < 10 ? `0${month}` : `${month}`,
          label: month < 10 ? `0${month}` : `${month}`,
        };
      });
    }

    if (yearNum === currentYearNum) {
      return Array.from({ length: 13 - currentMonth }, (_, i) => {
        const month = currentMonth + i;
        return {
          value: month < 10 ? `0${month}` : `${month}`,
          label: month < 10 ? `0${month}` : `${month}`,
        };
      });
    }

    return [];
  };

  const validMonths = getValidMonths(expYear);

  useEffect(() => {
    if (paymentProcessor?.name === 'fortis') {
      handleFortisToken();
    }
  }, []);

  useEffect(() => {
    const newValidYears = getValidYears(expMonth);

    if (expYear && !newValidYears.some(year => year.value === expYear)) {
      setExpYear('');
    }
  }, [expMonth, expYear]);

  useEffect(() => {
    const newValidMonths = getValidMonths(expYear);

    if (expMonth && !newValidMonths.some(month => month.value === expMonth)) {
      setExpMonth('');
    }
  }, [expYear, expMonth]);

  const formatCardNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    const limitedDigits = digits.slice(0, 19);
    const formattedValue = limitedDigits.replace(/(\d{4})(?=\d)/g, '$1 ');
    return formattedValue;
  };

  const handleCardNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    setCardNumber(formatted);
  };

  const handleNameOnCardChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
    setNameOnCard(value);
  };

  const handleCvcChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setCvc(value);
  };

  const handleExpMonthChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setExpMonth(e.target.value);
  };

  const handleExpYearChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setExpYear(e.target.value);
  };

  const handleSaveCard = async (paramsToSend: any) => {
    setIsLoading(true);

    await handleToastAsyncAction(
      async () => {
        const response = await createPaymentMethod({ params: paramsToSend });
        dispatch(addPaymentMethod(response));
        try {
          await setDefaultMethod({ id: response.id });
        } catch (error) {
          console.error('Error setting payment method as primary:', error);
        }
        return response;
      },
      {
        onSuccessMessage: t('payment.create.toastify.success'),
        onErrorMessage: (error) => {


          if (error?.response?.data) {
            const backendErrors = error.response.data;

            let humanReadableMessage = '';

            if (backendErrors.detail) {

              const detailText = backendErrors.detail;

              const cleanDetail = detailText.replace(/\s*\{[^}]*\}.*$/, '').trim();
              if (cleanDetail.endsWith('.')) {
                humanReadableMessage += cleanDetail;
              } else {
                humanReadableMessage += cleanDetail + '.';
              }
            }

            if (backendErrors.exceptionValue) {
              try {
                let cleanExceptionValue = backendErrors.exceptionValue;

                cleanExceptionValue = cleanExceptionValue.replace(/'/g, '"');

                const exceptionValue = JSON.parse(cleanExceptionValue);
                if (exceptionValue.cvc && exceptionValue.cvc[0] && exceptionValue.cvc[0].string) {
                  humanReadableMessage += ` - ${exceptionValue.cvc[0].string}`;
                }
              } catch (parseError) {
                const exceptionValue = backendErrors.exceptionValue;

                const messageMatch = exceptionValue.match(/string='([^']+)'/);
                if (messageMatch && messageMatch[1]) {
                  humanReadableMessage += ` - ${messageMatch[1]}`;
                } else {
                  const cleanException = exceptionValue.replace(/\{.*?ErrorDetail\(string='([^']+)'.*?\}/g, '$1');
                  humanReadableMessage += ` - ${cleanException}`;
                }
              }
            }


            return humanReadableMessage || t('payment.create.toastify.error');
          }

          return t('payment.create.toastify.error');
        },
        errorLogger: (error) => {
          console.error('Error creating payment method:', error);

          if (error?.response?.data) {
            const backendErrors = error.response.data;

            if (backendErrors.cvc) {
              setErrors(prev => ({
                ...prev,
                cvc: backendErrors.cvc,
              }));
            }

            if (backendErrors.number) {
              setErrors(prev => ({
                ...prev,
                number: backendErrors.number,
              }));
            }

            if (backendErrors.expMonth) {
              setErrors(prev => ({
                ...prev,
                expMonth: backendErrors.expMonth,
              }));
            }

            if (backendErrors.nameOnCard) {
              setErrors(prev => ({
                ...prev,
                nameOnCard: backendErrors.nameOnCard,
              }));
            }

            if (backendErrors.detail || backendErrors.exceptionValue) {
              let humanReadableMessage = '';
              
              if (backendErrors.detail) {
                const detailText = backendErrors.detail;
                const cleanDetail = detailText.replace(/\s*\{[^}]*\}.*$/, '').trim();
                if (cleanDetail.endsWith('.')) {
                  humanReadableMessage += cleanDetail;
                } else {
                  humanReadableMessage += cleanDetail + '.';
                }
              }
              
              if (backendErrors.exceptionValue) {
                try {
                  let cleanExceptionValue = backendErrors.exceptionValue.replace(/'/g, '"');
                  const exceptionValue = JSON.parse(cleanExceptionValue);
                  if (exceptionValue.cvc && exceptionValue.cvc[0] && exceptionValue.cvc[0].string) {
                    humanReadableMessage += ` - ${exceptionValue.cvc[0].string}`;
                  }
                } catch (parseError) {
                  const exceptionValue = backendErrors.exceptionValue;
                  const messageMatch = exceptionValue.match(/string='([^']+)'/);
                  if (messageMatch && messageMatch[1]) {
                    humanReadableMessage += ` - ${messageMatch[1]}`;
                  }
                }
              }
              
              if (humanReadableMessage) {
                setErrors(prev => ({
                  ...prev,
                  nonFieldErrors: [humanReadableMessage],
                }));
              }
            }
          }
        },
      }
    );

    props.setShowAddPayment(false);
    setIsLoading(false);
  };

  const handleFortisToken = async () => {
    setIsLoading(true);
    try {
      const fortisCall = await getFortisClientToken({
        url: paymentProcessor.config.apiUrl,
        locationId: paymentProcessor.config.locationId,
        userId: paymentProcessor.config.userId,
        apiKey: paymentProcessor.config.apiKey,
        developerId: paymentProcessor.config.developerId,
      });

      const CommerceElements = await new (window as any).Commerce.elements(fortisCall.data.client_token);

      await CommerceElements
        .create({
          container: '#payment_form',
          theme: 'default',
          showReceipt: false,
          environment: paymentProcessor.config.environment,
          appearance: {
            colorButtonBackground: primaryColor,
            colorButtonActionBackground: primaryColor,
          },
        });

      await CommerceElements
        .on('done', async (result: any) => {
          const paramsToSend = {
            ticket_id: result.data.id,
          };

          await handleSaveCard(paramsToSend);
        });

      await CommerceElements
        .on('error', (error: any) => {
          toast.error(`${t('payment.create.toastify.error')}`);
          setIsLoading(false);
        });
    } catch (error: any) {
      setIsLoading(false);
      throw new Error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      const rawCardNumber = cardNumber.replace(/\s/g, '');
      const newErrors: AddPaymentType = { ...defaultAddPaymentType };
      let hasErrors = false;

      if (rawCardNumber.length < 15 || rawCardNumber.length > 19) {
        newErrors.number.push(t('payment.errors.invalidCardNumber'));
        hasErrors = true;
      }

      if (cvc.length < 3 || cvc.length > 4) {
        newErrors.cvc.push(t('payment.errors.invalidCvc'));
        hasErrors = true;
      }

      if (!expMonth || !expYear) {
        newErrors.expMonth.push(t('payment.errors.requiredExpiration'));
        hasErrors = true;
      }

      if (hasErrors) {
        setErrors(newErrors);
        setIsLoading(false);
        return;
      }

      const paramsToSend = {
        number: rawCardNumber,
        expMonth: expMonth,
        expYear: expYear,
        cvc: cvc,
      };

      await handleSaveCard(paramsToSend);
    } catch (error: any) {
      if (error.response && error.response.status === 400) {
        setErrors({
          ...errors,
          ...error.response.data,
        });
      } else {
        console.error('Error creating payment method:', error);
        toast.error(`${t('payment.create.toastify.error')}`);

        Sentry.captureException(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (paymentProcessor?.name === 'fortis') {
    return (
      <>
        {isLoading && (
          <SpinnerOverlay>
            <Spinner />
          </SpinnerOverlay>
        )}
        <div id="payment_form" />
      </>
    );
  }

  return (
    <>
      {isLoading && (
        <SpinnerOverlay>
          <Spinner />
        </SpinnerOverlay>
      )}
      <form onSubmit={onSubmit}>
        <Wrapper>
          <Title>
            {t('payment.addPayMethods.title')}
          </Title>
          <FullRow>
            <Input
              label={t('payment.nameOnCard.title')}
              name="nameOnCard"
              type="text"
              placeholder={t('payment.nameOnCard.placeholder')}
              error={errors.nameOnCard}
              value={nameOnCard}
              onChange={handleNameOnCardChange}
              required
            />
          </FullRow>
          <FullRow>
            <Input
              label={t('payment.cardNumber.title')}
              name="number"
              type="text"
              placeholder={t('payment.cardNumber.placeholder')}
              error={errors.number}
              value={cardNumber}
              onChange={handleCardNumberChange}
              maxLength={23}
              required
            />
          </FullRow>
          <HalfRow>
            <InputWrapper>
              <Label>{t('payment.expDate.title')}</Label>
              <ExpirationDateWrapper>
                <MonthSelect
                  name="expMonth"
                  value={expMonth}
                  onChange={handleExpMonthChange}
                  required
                  defaultValue=""
                >
                  <option value="" disabled>MM</option>
                  {validMonths.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </MonthSelect>
                <YearSelect
                  name="expYear"
                  value={expYear}
                  onChange={handleExpYearChange}
                  required
                  defaultValue=""
                >
                  <option value="" disabled>YYYY</option>
                  {validYears.map((year) => (
                    <option key={year.value} value={year.value}>
                      {year.label}
                    </option>
                  ))}
                </YearSelect>
              </ExpirationDateWrapper>
              {errors.expMonth.length > 0 && (
                <InputErrorText>{errors.expMonth[0]}</InputErrorText>
              )}
            </InputWrapper>
            <Input
              label={t('payment.cvv.title')}
              name="cvc"
              placeholder={t('payment.cvv.placeholder')}
              type="text"
              maxLength={4}
              value={cvc}
              onChange={handleCvcChange}
              error={errors.cvc}
              required
            />
          </HalfRow>
          {errors.nonFieldErrors.length ? <ErrorText>{errors.nonFieldErrors[0]}</ErrorText> : null}
        </Wrapper>
        <ButtonsWrapper>
          <CancelButton
            title={t('shared.action.cancel')}
            type="reset"
            onClick={() => props.setShowAddPayment(false)}
            disabled={isLoading}
          />
          <Button
            title={t('shared.action.save')}
            type="submit"
            disabled={isLoading}
          />
        </ButtonsWrapper>
      </form>
    </>
  );
};